import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type {
  AccessRequestProvisioned,
  AccessRequestView,
  InviteAccessRequestInput,
} from '../lib/admin-access-requests';
import { absolute } from '../lib/time';
import { AdminInviteForm } from './AdminInviteForm';
import { CopySnippet } from './CopySnippet';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// The invite dialog (arc 101 slice 01): the provision form (`<AdminInviteForm/>`,
// prefilled from `DEFAULT_INVITE_BUNDLE`), then — once the write lands — the result:
// the one-time accept link with a copy affordance, and the mail-on-its-way line.
//
// PREFILLED, AND STILL SENT. The API defaults every knob to the bundle, so an
// empty body would provision the same thing; the form sends the numbers it
// shows so the audit row records what the operator SAW, and a departure from
// the bundle is a value they typed rather than one they failed to notice.
//
// THE RESULT REPLACES THE FORM, not a toast: the accept link is shown once and
// nowhere else (`InvitationView` carries no token), so it needs a surface the
// operator dismisses deliberately. The mail is best-effort server-side
// (`apps/api/src/deps/invitations.ts`), and the answer says whether it LEFT
// (`mailed`, deep sweep 03-13): "on its way" only then, never a claim of
// delivery, and a send that failed says so over the link. A refused write keeps
// the form open over the API's own `detail`.

export function AdminInviteDialog(props: {
  /** The application the dialog is open for; null = closed. */
  readonly request: AccessRequestView | null;
  readonly busy: boolean;
  readonly error: string | null;
  /** The provision result once the invite landed; the form until then. */
  readonly result: AccessRequestProvisioned | null;
  readonly onSend: (body: InviteAccessRequestInput) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const email = (): string => props.request?.email ?? '';

  return (
    <Modal
      open={props.request !== null}
      size="lg"
      title={
        props.result === null
          ? t('admin.access_invite_title', { name: props.request?.name ?? '' })
          : t('admin.access_invited_title')
      }
      {...(props.result === null
        ? { desc: t('admin.access_invite_desc', { email: email() }) }
        : {})}
      onClose={props.onClose}
      footer={
        <Show
          when={props.result === null}
          fallback={<Button onClick={props.onClose}>{t('admin.access_done')}</Button>}
        >
          <Button variant="outline" disabled={props.busy} onClick={props.onClose}>
            {t('admin.access_cancel')}
          </Button>
          {/* A native submit bound to the form by id — `<Button/>` pins
              `type="button"`, and the form's own submit is what lets Enter in a
              field send. */}
          <button
            type="submit"
            form="admin-invite-form"
            class={buttonClass()}
            disabled={props.busy}
          >
            {t(props.busy ? 'admin.access_sending' : 'admin.access_send')}
          </button>
        </Show>
      }
    >
      <Show
        when={props.result}
        keyed
        fallback={
          // Keyed on the application: a fresh form (bundle defaults) per open.
          <Show when={props.request} keyed>
            {(_request) => (
              <AdminInviteForm busy={props.busy} error={props.error} onSend={props.onSend} />
            )}
          </Show>
        }
      >
        {(result) => (
          <div class="field-group">
            {/* The derived box budget, read off the server's applied bundle —
                the one place it exists for this screen (`lib/billing.ts`
                cannot import the formula). */}
            <p class="field__desc" data-testid="invite-box-budget-applied">
              {t('admin.access_invited_boxes', { boxes: result.bundle.boxes })}
            </p>
            <Show
              when={result.invitation}
              keyed
              fallback={<p role="status">{t('admin.access_invited_registered')}</p>}
            >
              {(invitation) => (
                <>
                  <p role="status">
                    {t(
                      invitation.mailed
                        ? 'admin.access_invited_mail'
                        : 'admin.access_invited_not_mailed',
                      { email: email() },
                    )}
                  </p>
                  <Show when={invitation.reissued}>
                    <p class="field__desc">{t('admin.access_invited_reissued')}</p>
                  </Show>
                  <CopySnippet
                    label={t('admin.access_accept_link')}
                    snippet={invitation.acceptUrl}
                  />
                  <p class="field__desc">
                    {t('admin.access_invited_expires', { at: absolute(invitation.expiresAtIso) })}
                  </p>
                </>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Modal>
  );
}
